import React from 'react'
import { Label } from '@hospitalrun/components'
import classes from '../../style.module.scss'
import Select from 'react-select'
import { useTranslation } from 'react-i18next'

interface Option {
  title?: string | undefined
  label: string
  value: string | number | Object
}

interface Props {
  value: string | Option[] | { label: string; value: any } | null
  label: string
  name: string
  isEditable?: boolean
  isRequired?: boolean
  isInvalid?: boolean
  isMulti?: boolean
  feedback?: string
  options: Option[]
  onChange?: (option: any) => void
  placeholder?: string
  disabled?: boolean
}

const ReactSelectWithLabelFormGroup = (props: Props) => {
  const {
    value,
    label,
    name,
    isEditable,
    options,
    onChange,
    isRequired,
    isInvalid,
    isMulti,
    feedback,
    placeholder,
    disabled,
  } = props
  const id = `${name}Select`
  const { t } = useTranslation()

  /**
   * The read-only rendering of whatever `value` currently is.
   *
   * `value` is declared as a string, an array of options, or a *single* option —
   * single-select callers pass one object, not a one-element array. Only the first
   * two were handled, so a single-select field threw "value.map is not a function"
   * the moment it was viewed rather than edited. Null entries are skipped because
   * callers map over ids that may not resolve to a group yet.
   */
  const getValueAsString = (value: any) => {
    if (!value) {
      return
    }

    if (typeof value === 'string') return value

    if (Array.isArray(value)) {
      return (value as Option[])
        .filter(Boolean)
        .map(val => val.label)
        .join(', ')
    }

    return (value as Option).label
  }

  return (
    <div className="form-group">
      <Label
        className={disabled ? classes.disabled_label : classes.label}
        text={label}
        htmlFor={id}
        isRequired={isRequired && isEditable}
      />
      {isEditable ? (
        <>
          <Select
            className="react-select"
            classNamePrefix="react-select"
            isMulti={isMulti}
            isDisabled={!isEditable || disabled}
            isSearchable={true}
            onChange={onChange}
            isRequired={isRequired}
            isInvalid={isInvalid}
            options={
              !isMulti && value
                ? [{ value: null, label: `-- ${t('patient.choose')} --` }, ...options]
                : options
            }
            placeholder={placeholder ? placeholder : `-- ${t('patient.choose')} --`}
            defaultValue={value as any}
            value={value as any}
          />
          <div className={'text-left ml-3 mt-1 text-small text-danger'}>
            {isInvalid ? feedback : ''}
          </div>
        </>
      ) : (
        <div>
          <pre className="text-secondary">{getValueAsString(value)}</pre>
          <br />
        </div>
      )}
    </div>
  )
}

ReactSelectWithLabelFormGroup.defaultProps = {
  value: '',
}

export default ReactSelectWithLabelFormGroup
